import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { FetchError } from 'ofetch';

const findErrorMessage = (res: unknown): string | null => {
  if (typeof res === 'string' && res) {
    return res;
  }
  if (typeof res === 'object' && Array.isArray(res) && res.length) {
    return findErrorMessage(res[0]);
  }
  if (typeof res === 'object' && res) {
    return 'message' in res
      ? findErrorMessage(res.message)
      : 'detail' in res
        ? findErrorMessage(res.detail)
        : 'error' in res
          ? findErrorMessage(res.error)
          : 'errors' in res
            ? findErrorMessage(res.errors)
            : null;
  }
  return null;
};

// The backend mixes prose meant for users with machine codes like
// `insufficient_balance` or `RATE_LIMITED`. Only the prose is worth showing;
// everything else falls back to our own copy for that status.
const isUserFacing = (message: string) => {
  const trimmed = message.trim();
  if (trimmed.length < 3 || trimmed.length > 300) return false;
  if (/^[a-z0-9]+(?:[_.-][a-z0-9]+)+$/i.test(trimmed)) return false;
  if (/^[A-Z][A-Z0-9_]*$/.test(trimmed)) return false;
  return true;
};

const byStatus = (status: number) => {
  if (status === 408 || status === 504)
    return {
      title: msg`This is taking longer than usual`,
      message: msg`The request timed out. Please try again.`,
    };
  if (status === 401)
    return {
      title: msg`Your session has expired`,
      message: msg`Please sign in again to continue.`,
    };
  if (status === 403)
    return {
      title: msg`You don't have access to this`,
      message: msg`If you think this is a mistake, please contact support.`,
    };
  if (status === 404)
    return {
      title: msg`We couldn't find that`,
      message: msg`It may have been moved or removed.`,
    };
  if (status === 409)
    return {
      title: msg`Something changed while you were working`,
      message: msg`Refresh the page to get the latest version, then try again.`,
    };
  if (status === 429)
    return {
      title: msg`Too many attempts`,
      message: msg`Please wait a moment before trying again.`,
    };
  if (status >= 500)
    return {
      title: msg`Something went wrong on our side`,
      message: msg`Our team has been notified. Please try again in a moment.`,
    };
  if (status >= 400)
    return {
      title: msg`We couldn't complete that`,
      message: msg`Please review your details and try again.`,
    };
  return {
    title: msg`Something went wrong`,
    message: msg`Please try again. If this keeps happening, contact support.`,
  };
};

export const parseError = (
  error: unknown,
): { title: string; message: string } => {
  try {
    if (error instanceof FetchError) {
      const status = error.status ?? 0;

      // A FetchError with no status never reached the server.
      if (!status) {
        const offline =
          typeof navigator !== 'undefined' && navigator.onLine === false;
        return offline
          ? {
              title: i18n._(msg`You're offline`),
              message: i18n._(
                msg`Check your internet connection and try again.`,
              ),
            }
          : {
              title: i18n._(msg`We couldn't reach the server`),
              message: i18n._(
                msg`Check your connection and try again in a moment.`,
              ),
            };
      }

      const fallback = byStatus(status);
      const fromBackend = status >= 500 ? null : findErrorMessage(error.data);

      return {
        title: i18n._(fallback.title),
        message:
          fromBackend && isUserFacing(fromBackend)
            ? fromBackend
            : i18n._(fallback.message),
      };
    }

    // Anything else is a bug in our own code — the raw message would only
    // confuse the user, so it goes to Sentry and they see plain copy.
    return {
      title: i18n._(msg`Something went wrong`),
      message: i18n._(
        msg`Please try again. If this keeps happening, contact support.`,
      ),
    };
  } catch {
    return {
      title: i18n._(msg`Something went wrong`),
      message: i18n._(
        msg`Please try again. If this keeps happening, contact support.`,
      ),
    };
  }
};
